import React, {useEffect, useRef, useState} from "react";
import {Box, Card, CardContent, Grid, Typography} from "@mui/material";
import StatItem from "./ui/StatItem";
import StrategyStatItem from "./ui/StrategyStatItem";
import WinnerLoserBox from "./ui/WinnerLoserBox";

export const getNextMonthsPnL = (positions) => {
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  const monthsMap = new Map();

  positions.forEach((position) => {
    if (!position.closing_date) return;

    const closeDate = new Date(position.closing_date);
    if (Number.isNaN(closeDate.getTime())) return;

    const closeMonth = closeDate.getMonth();
    const closeYear = closeDate.getFullYear();

    // Forward-looking only: skip anything before the current month.
    if (
      closeYear < currentYear ||
      (closeYear === currentYear && closeMonth < currentMonth)
    ) {
      return;
    }

    const key = `${closeYear}-${closeMonth}`;
    if (!monthsMap.has(key)) {
      const date = new Date(closeYear, closeMonth, 1);
      monthsMap.set(key, {
        month: closeMonth,
        year: closeYear,
        monthYear: date.toLocaleString("default", {
          month: "short",
          year: "numeric",
        }),
        grossPnL: 0,
        netPnL: 0,
        deals: 0,
        strategies: [],
      });
    }

    const monthEntry = monthsMap.get(key);
    monthEntry.grossPnL += Number(position.gross_pnl_to_dealclose) || 0;
    monthEntry.netPnL += Number(position.net_pnl_to_dealclose) || 0;
    monthEntry.deals += 1;

    const strategyName = position.StrategyName || "Unknown";
    const existingStrategy = monthEntry.strategies.find(
      (s) => s.name === strategyName
    );

    if (existingStrategy) {
      existingStrategy.grossPnL += Number(position.gross_pnl_to_dealclose) || 0;
      existingStrategy.netPnL += Number(position.net_pnl_to_dealclose) || 0;
      existingStrategy.deals += 1;
    } else {
      monthEntry.strategies.push({
        name: strategyName,
        grossPnL: Number(position.gross_pnl_to_dealclose) || 0,
        netPnL: Number(position.net_pnl_to_dealclose) || 0,
        deals: 1,
      });
    }
  });

  const nextMonths = Array.from(monthsMap.values()).sort(
    (a, b) => a.year - b.year || a.month - b.month
  );

  nextMonths.forEach((month) => {
    month.strategies.sort((a, b) => b.netPnL - a.netPnL);
  });

  return nextMonths;
};

const MABSummaryStats = ({
  MABData,
  NAV_NUMBER,
  arb_monitor_data,
  strategyAllowedRegionsData,
  strategyRegionsData,
}) => {
  const calculateStats = () => {
    if (!MABData)
      return {
        longExposure: 0,
        shortExposure: 0,
        grossExposure: 0,
        netExposure: 0,
        weightedDuration: 0,
      };

    const today = new Date();
    let totalLMVWithDates = 0;
    let weightedDurationSum = 0;

    const stats = MABData.reduce(
      (acc, position) => {
        // Calculate LMV and SMV as before
        acc.longExposure += position.LMV || 0;
        acc.shortExposure += position.SMV || 0;

        // Calculate weighted duration only for positions with valid closing dates and positive LMV
        if (position.closing_date && position.LMV > 0) {
          const closeDate = new Date(position.closing_date);
          const durationInDays = (closeDate - today) / (1000 * 60 * 60 * 24); // Convert to days

          weightedDurationSum += durationInDays * position.LMV;
          totalLMVWithDates += position.LMV;
        }

        return acc;
      },
      {
        longExposure: 0,
        shortExposure: 0,
      }
    );

    // Calculate gross and net exposure
    stats.grossExposure = stats.longExposure + Math.abs(stats.shortExposure);
    stats.netExposure = stats.longExposure + stats.shortExposure;

    // Calculate weighted average duration (in days)
    stats.weightedDuration =
      totalLMVWithDates > 0 ? weightedDurationSum / totalLMVWithDates : 0;

    return stats;
  };

  // New function to calculate strategy-level statistics
  const calculateStrategyStats = () => {
    // Group by strategy and calculate totals
    const strategyStats = MABData.reduce((acc, position) => {
      const strategy = position.StrategyName || "Unknown";
      const lmv = position.LMV || 0;
      const risk = Number(position.risk_to_downside) || 0;
      const pnl = position.total_pnl || 0;

      if (!acc[strategy]) {
        acc[strategy] = {
          name: strategy,
          lmv: 0,
          risk: 0,
          pnl: 0,
          numPositions: 0,
        };
      }

      acc[strategy].lmv += lmv;
      acc[strategy].risk += risk;
      acc[strategy].pnl += pnl;
      acc[strategy].numPositions += 1;

      return acc;
    }, {});

    const strategyArray = Object.values(strategyStats);

    return {
      // Top strategies by LMV
      topSize: strategyArray
        .sort((a, b) => Math.abs(b.lmv) - Math.abs(a.lmv))
        .slice(0, 5000),
      // Top strategies by risk (most negative first)
      topRisk: strategyArray.sort((a, b) => a.risk - b.risk).slice(0, 5000),
      // Top strategies by PnL
      topPnL: strategyArray.sort((a, b) => b.pnl - a.pnl).slice(0, 5000),
      // Bottom strategies by PnL
      bottomPnL: strategyArray.sort((a, b) => a.pnl - b.pnl).slice(0, 5000),
    };
  };

  // New function to calculate geography-based LMV aggregation
  const calculateGeographyStats = () => {
    if (!MABData || !strategyAllowedRegionsData || !strategyRegionsData) {
      return [];
    }

    // Get all available regions from strategyAllowedRegionsData in their original order
    const availableRegions = strategyAllowedRegionsData.map(
      (region) => region.region
    );

    // Create a map of strategy names to their regions
    const strategyToRegionMap = {};
    strategyRegionsData.forEach((item) => {
      strategyToRegionMap[item.StrategyName] = item.Region;
    });

    // Calculate LMV by region and collect strategy details
    const regionStats = {};

    // Initialize all available regions with 0 LMV and empty strategies array
    availableRegions.forEach((region) => {
      regionStats[region] = {
        region: region,
        lmv: 0,
        strategies: [],
      };
    });

    // Add "Untagged" category
    regionStats["Untagged"] = {
      region: "Untagged",
      lmv: 0,
      strategies: [],
    };

    // Aggregate LMV by region and collect strategy details
    MABData.forEach((position) => {
      const strategyName = position.StrategyName;
      const region = strategyToRegionMap[strategyName];
      const lmv = position.LMV || 0;

      if (region && regionStats[region]) {
        regionStats[region].lmv += lmv;

        // Add strategy details
        const existingStrategy = regionStats[region].strategies.find(
          (s) => s.name === strategyName
        );
        if (existingStrategy) {
          existingStrategy.lmv += lmv;
          existingStrategy.numPositions += 1;
        } else {
          regionStats[region].strategies.push({
            name: strategyName,
            lmv: lmv,
            numPositions: 1,
          });
        }
      } else {
        // If no region found, add to Untagged
        regionStats["Untagged"].lmv += lmv;

        // Add strategy details to Untagged
        const existingStrategy = regionStats["Untagged"].strategies.find(
          (s) => s.name === strategyName
        );
        if (existingStrategy) {
          existingStrategy.lmv += lmv;
          existingStrategy.numPositions += 1;
        } else {
          regionStats["Untagged"].strategies.push({
            name: strategyName,
            lmv: lmv,
            numPositions: 1,
          });
        }
      }
    });

    // Calculate total LMV for percentage calculation
    const totalLMV = Object.values(regionStats).reduce(
      (sum, stat) => sum + stat.lmv,
      0
    );

    // Sort strategies within each region by LMV (descending)
    Object.values(regionStats).forEach((region) => {
      region.strategies.sort((a, b) => Math.abs(b.lmv) - Math.abs(a.lmv));

      // Add percentage and formatted LMV to each strategy
      region.strategies.forEach((strategy) => {
        strategy.percentage =
          totalLMV > 0 ? ((strategy.lmv / totalLMV) * 100).toFixed(1) : 0;
        strategy.formattedLMV = strategy.lmv.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
      });
    });

    // Create ordered array: available regions first (in their original order), then Untagged
    const geographyStats = [
      // First add all available regions in their original order (including zero LMV)
      ...availableRegions.map((region) => ({
        ...regionStats[region],
        percentage:
          totalLMV > 0
            ? ((regionStats[region].lmv / totalLMV) * 100).toFixed(1)
            : 0,
        formattedLMV: regionStats[region].lmv.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }),
      })),
      // Then add Untagged if it has any LMV (including zero)
      ...(regionStats["Untagged"].lmv >= 0
        ? [
            {
              ...regionStats["Untagged"],
              percentage:
                totalLMV > 0
                  ? ((regionStats["Untagged"].lmv / totalLMV) * 100).toFixed(1)
                  : 0,
              formattedLMV: regionStats["Untagged"].lmv.toLocaleString(
                "en-US",
                {
                  style: "currency",
                  currency: "USD",
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }
              ),
            },
          ]
        : []),
    ];

    return geographyStats;
  };

  const stats = calculateStats();
  const strategyStats = calculateStrategyStats();
  const geographyStats = calculateGeographyStats();

  // Initialize state for expanded strategies from localStorage
  const [expandedSizeStrategies, setExpandedSizeStrategies] = useState(() => {
    const saved = localStorage.getItem("mabSizeExpandedStrategies");
    return saved ? JSON.parse(saved) : {};
  });

  const [expandedRiskStrategies, setExpandedRiskStrategies] = useState(() => {
    const saved = localStorage.getItem("mabRiskExpandedStrategies");
    return saved ? JSON.parse(saved) : {};
  });

  // Initialize state for expanded regions from localStorage
  const [expandedRegions, setExpandedRegions] = useState(() => {
    const saved = localStorage.getItem("mabExpandedRegions");
    return saved ? JSON.parse(saved) : {};
  });

  // Initialize state for expanded monthly PnL breakdowns from localStorage
  const [expandedMonthlyPnL, setExpandedMonthlyPnL] = useState(() => {
    const saved = localStorage.getItem("mabExpandedMonthlyPnL");
    return saved ? JSON.parse(saved) : {};
  });

  // References for scroll containers
  const sizeListRef = useRef(null);
  const riskListRef = useRef(null);

  // Save expanded states when they change
  useEffect(() => {
    localStorage.setItem(
      "mabSizeExpandedStrategies",
      JSON.stringify(expandedSizeStrategies)
    );
  }, [expandedSizeStrategies]);

  useEffect(() => {
    localStorage.setItem(
      "mabRiskExpandedStrategies",
      JSON.stringify(expandedRiskStrategies)
    );
  }, [expandedRiskStrategies]);

  // Save expanded regions state when it changes
  useEffect(() => {
    localStorage.setItem("mabExpandedRegions", JSON.stringify(expandedRegions));
  }, [expandedRegions]);

  // Save expanded monthly PnL state when it changes
  useEffect(() => {
    localStorage.setItem(
      "mabExpandedMonthlyPnL",
      JSON.stringify(expandedMonthlyPnL)
    );
  }, [expandedMonthlyPnL]);

  // Handle scroll events
  const handleSizeScroll = () => {
    if (sizeListRef.current) {
      const scrollTop = sizeListRef.current.scrollTop;
      localStorage.setItem("mabSizeListScrollPosition", scrollTop.toString());
    }
  };

  const handleRiskScroll = () => {
    if (riskListRef.current) {
      const scrollTop = riskListRef.current.scrollTop;
      localStorage.setItem("mabRiskListScrollPosition", scrollTop.toString());
    }
  };

  // Restore scroll positions on mount and when data changes
  useEffect(() => {
    const restoreScrollPositions = () => {
      const sizeScrollPosition = localStorage.getItem(
        "mabSizeListScrollPosition"
      );
      const riskScrollPosition = localStorage.getItem(
        "mabRiskListScrollPosition"
      );

      if (sizeListRef.current && sizeScrollPosition) {
        sizeListRef.current.scrollTop = parseInt(sizeScrollPosition, 10);
      }
      if (riskListRef.current && riskScrollPosition) {
        riskListRef.current.scrollTop = parseInt(riskScrollPosition, 10);
      }
    };

    // Wait for content to be fully rendered
    const timeoutId = setTimeout(restoreScrollPositions, 500);
    return () => clearTimeout(timeoutId);
  }, [MABData]);

  // Clear scroll positions when component unmounts
  useEffect(() => {
    return () => {
      // Only clear if scroll position is at or near top
      const sizeList = sizeListRef?.current;
      const riskList = riskListRef?.current;
      if (sizeList && sizeList.scrollTop < 10) {
        localStorage.removeItem("mabSizeListScrollPosition");
      }
      if (riskList && riskList.scrollTop < 10) {
        localStorage.removeItem("mabRiskListScrollPosition");
      }
    };
  }, []);

  // Helper function for getting strategy details
  const getStrategyDetails = (strategyName) => {
    return MABData?.filter(
      (position) => position.StrategyName === strategyName
    )?.map((position) => ({
      securityName: position.security_name,
      marketValue: position.market_value || 0,
      percentOfNav: ((position.market_value || 0) / NAV_NUMBER) * 100,
      risk: position.risk_to_downside || 0,
      riskBps: ((position.risk_to_downside || 0) / NAV_NUMBER) * 10000,
    }));
  };

  // Helper function to get strategy PnL data
  const getStrategyPnL = (positions) => {
    const strategyStats = positions.reduce((acc, position) => {
      const strategy = position.StrategyName || "Unknown";
      if (!acc[strategy]) {
        acc[strategy] = {
          name: strategy,
          pnl: 0,
          numPositions: 0,
        };
      }
      acc[strategy].pnl += Number(position.total_pnl) || 0;
      acc[strategy].numPositions += 1;
      return acc;
    }, {});

    return Object.values(strategyStats);
  };

  // Helper function to format amounts
  const formatAmount = (amount) => {
    const absAmount = Math.abs(amount);
    if (absAmount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}mm`;
    } else if (absAmount >= 1000) {
      return `${(amount / 1000).toFixed(1)}k`;
    }
    return amount.toFixed(1);
  };

  return (
    <Box
      sx={{
        padding: "6px",
        backgroundColor: "#f8f9fa",
        minHeight: "calc(100vh - 130px)",
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "6px",
          padding: "16px 24px",
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          border: "1px solid rgba(0,0,0,0.05)",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "#1a237e",
            fontSize: "1.5rem",
            letterSpacing: "-0.5px",
          }}
        >
          MAB Dashboard
        </Typography>
        {arb_monitor_data[0]?.updated_at && (
          <Typography
            variant="subtitle2"
            sx={{
              color: "#64748b",
              fontSize: "0.9rem",
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span style={{ fontSize: "1.2rem" }}>•</span>
            Last updated:{" "}
            {new Date(arb_monitor_data[0].updated_at).toLocaleTimeString()}
          </Typography>
        )}
      </Box>

      {/* Main Content Grid */}
      <Grid container spacing={1}>
        {/* Left Column - Key Metrics */}
        <Grid item xs={12} md={2}>
          <Grid container spacing={1}>
            {/* Exposure Statistics Card */}
            <Grid item xs={12}>
              <Card
                sx={{
                  backgroundColor: "#ffffff",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                  borderRadius: "16px",
                  transition: "all 0.3s ease-in-out",
                  border: "1px solid rgba(0,0,0,0.05)",
                  "&:hover": {
                    boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent sx={{ padding: "16px" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      marginBottom: "20px",
                      fontSize: "1.1rem",
                      fontWeight: 700,
                      color: "#1a237e",
                      display: "flex",
                      alignItems: "center",
                      "&:before": {
                        content: '""',
                        display: "block",
                        width: "4px",
                        height: "24px",
                        backgroundColor: "#1a237e",
                        marginRight: "12px",
                        borderRadius: "2px",
                      },
                    }}
                  >
                    Exposure Statistics
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <StatItem
                      label="Long Exposure"
                      amount={stats.longExposure}
                      percentage={(stats.longExposure / NAV_NUMBER) * 100}
                    />
                    <StatItem
                      label="Short Exposure"
                      amount={stats.shortExposure}
                      percentage={(stats.shortExposure / NAV_NUMBER) * 100}
                      isNegative={true}
                    />
                    <StatItem
                      label="Gross Exposure"
                      amount={stats.grossExposure}
                      percentage={(stats.grossExposure / NAV_NUMBER) * 100}
                    />
                    <StatItem
                      label="Net Exposure"
                      amount={stats.netExposure}
                      percentage={(stats.netExposure / NAV_NUMBER) * 100}
                      isNegative={stats.netExposure < 0}
                    />
                    <StatItem
                      label="LMV Wgt. Duration"
                      amount={Math.round(stats.weightedDuration)}
                      percentage={0}
                      suffix=" days"
                      hidePercentage={true}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Position by Geography Card */}
            <Grid item xs={12}>
              <Card
                sx={{
                  backgroundColor: "#ffffff",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                  borderRadius: "16px",
                  transition: "all 0.3s ease-in-out",
                  border: "1px solid rgba(0,0,0,0.05)",
                  "&:hover": {
                    boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent sx={{ padding: "16px" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      marginBottom: "20px",
                      fontSize: "1.1rem",
                      fontWeight: 700,
                      color: "#1a237e",
                      display: "flex",
                      alignItems: "center",
                      "&:before": {
                        content: '""',
                        display: "block",
                        width: "4px",
                        height: "24px",
                        backgroundColor: "#1a237e",
                        marginRight: "12px",
                        borderRadius: "2px",
                      },
                    }}
                  >
                    Position by Geography
                  </Typography>
                  {geographyStats.length > 0 ? (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {geographyStats.map((region, index) => (
                        <Box
                          key={region.region}
                          sx={{
                            border: "1px solid #e2e8f0",
                            borderRadius: "6px",
                            overflow: "hidden",
                          }}
                        >
                          {/* Region Header - Clickable */}
                          <Box
                            onClick={() =>
                              setExpandedRegions((prev) => ({
                                ...prev,
                                [region.region]: !prev[region.region],
                              }))
                            }
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "6px 10px",
                              backgroundColor:
                                index % 2 === 0 ? "#f8fafc" : "transparent",
                              cursor: "pointer",
                              transition: "background-color 0.2s ease",
                              "&:hover": {
                                backgroundColor: "#e2e8f0",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: "#1e293b",
                                  fontSize: "0.8rem",
                                }}
                              >
                                {region.region}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#64748b",
                                  fontSize: "0.6rem",
                                  backgroundColor: "#e2e8f0",
                                  padding: "2px 6px",
                                  borderRadius: "10px",
                                }}
                              >
                                {region.strategies.length} deals
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Box sx={{ textAlign: "right" }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    color: "#1e293b",
                                    fontSize: "0.8rem",
                                  }}
                                >
                                  {region.formattedLMV}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#64748b",
                                    fontSize: "0.7rem",
                                    fontWeight: 500,
                                  }}
                                >
                                  {region.percentage}% of LMV
                                </Typography>
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#64748b",
                                  fontSize: "0.8rem",
                                  fontWeight: 600,
                                  transform: expandedRegions[region.region]
                                    ? "rotate(90deg)"
                                    : "rotate(0deg)",
                                  transition: "transform 0.2s ease",
                                }}
                              >
                                ▶
                              </Typography>
                            </Box>
                          </Box>

                          {/* Expanded Strategy Details */}
                          {expandedRegions[region.region] &&
                            region.strategies.length > 0 && (
                              <Box
                                sx={{
                                  backgroundColor: "#ffffff",
                                  borderTop: "1px solid #e2e8f0",
                                  padding: "8px",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                  }}
                                >
                                  {region.strategies.map(
                                    (strategy, strategyIndex) => (
                                      <Box
                                        key={strategy.name}
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          padding: "4px 8px",
                                          backgroundColor:
                                            strategyIndex % 2 === 0
                                              ? "#f8fafc"
                                              : "transparent",
                                          borderRadius: "4px",
                                          border: "1px solid #e2e8f0",
                                        }}
                                      >
                                        <Box>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 600,
                                              color: "#475569",
                                              fontSize: "0.7rem",
                                            }}
                                          >
                                            {strategy.name}
                                          </Typography>
                                        </Box>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "flex-end",
                                            gap: 0.25,
                                            textAlign: "right",
                                          }}
                                        >
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 700,
                                              color: "#475569",
                                              fontSize: "0.7rem",
                                            }}
                                          >
                                            {strategy.formattedLMV}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: "#64748b",
                                              fontSize: "0.6rem",
                                              fontWeight: 500,
                                            }}
                                          >
                                            {strategy.percentage}% of total
                                          </Typography>
                                        </Box>
                                      </Box>
                                    )
                                  )}
                                </Box>
                              </Box>
                            )}

                          {/* Show message if no strategies */}
                          {expandedRegions[region.region] &&
                            region.strategies.length === 0 && (
                              <Box
                                sx={{
                                  backgroundColor: "#ffffff",
                                  borderTop: "1px solid #e2e8f0",
                                  padding: "8px",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#64748b",
                                    fontStyle: "italic",
                                    textAlign: "center",
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  No strategies in this region
                                </Typography>
                              </Box>
                            )}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#64748b",
                        fontStyle: "italic",
                        textAlign: "center",
                        padding: "16px 0",
                      }}
                    >
                      No geography data available
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Center Column - Strategy Analysis */}
        <Grid item xs={12} md={5.5}>
          <Grid container spacing={1}>
            {/* Strategy Size Card */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  backgroundColor: "#ffffff",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                  borderRadius: "16px",
                  height: "95%",
                  transition: "all 0.3s ease-in-out",
                  border: "1px solid rgba(0,0,0,0.05)",
                  "&:hover": {
                    boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent sx={{ padding: "16px" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      marginBottom: "16px",
                      fontSize: "1.1rem",
                      fontWeight: 700,
                      color: "#1a237e",
                      display: "flex",
                      alignItems: "center",
                      "&:before": {
                        content: '""',
                        display: "block",
                        width: "4px",
                        height: "24px",
                        backgroundColor: "#1a237e",
                        marginRight: "12px",
                        borderRadius: "2px",
                      },
                    }}
                  >
                    Top Strategies by Size
                  </Typography>
                  <Box
                    ref={sizeListRef}
                    onScroll={handleSizeScroll}
                    sx={{
                      maxHeight: "600px",
                      overflow: "auto",
                      overflowX: "hidden",
                      paddingRight: "8px",
                      scrollBehavior: "smooth",
                      "&::-webkit-scrollbar": {
                        width: "6px",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: "#f1f1f1",
                        borderRadius: "3px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#cbd5e1",
                        borderRadius: "3px",
                      },
                      "&::-webkit-scrollbar-thumb:hover": {
                        background: "#94a3b8",
                      },
                    }}
                  >
                    {strategyStats.topSize?.map((strategy, index) => (
                      <StrategyStatItem
                        key={`size-${index}`}
                        name={strategy.name}
                        amount={strategy.lmv}
                        percentage={(strategy.lmv / NAV_NUMBER) * 100}
                        positions={strategy.numPositions}
                        isNegative={strategy.lmv < 0}
                        isRisk={false}
                        rank={index + 1}
                        isExpanded={expandedSizeStrategies[strategy.name]}
                        onToggleExpand={() =>
                          setExpandedSizeStrategies((prev) => ({
                            ...prev,
                            [strategy.name]: !prev[strategy.name],
                          }))
                        }
                        details={getStrategyDetails(strategy.name)}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Strategy Risk Card */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  backgroundColor: "#ffffff",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                  borderRadius: "16px",
                  height: "95%",
                  transition: "all 0.3s ease-in-out",
                  border: "1px solid rgba(0,0,0,0.05)",
                  "&:hover": {
                    boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent sx={{ padding: "16px" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      marginBottom: "16px",
                      fontSize: "1.1rem",
                      fontWeight: 700,
                      color: "#1a237e",
                      display: "flex",
                      alignItems: "center",
                      "&:before": {
                        content: '""',
                        display: "block",
                        width: "4px",
                        height: "24px",
                        backgroundColor: "#1a237e",
                        marginRight: "12px",
                        borderRadius: "2px",
                      },
                    }}
                  >
                    Top Strategies by Risk
                  </Typography>
                  <Box
                    ref={riskListRef}
                    onScroll={handleRiskScroll}
                    sx={{
                      maxHeight: "600px",
                      overflow: "auto",
                      overflowX: "hidden",
                      paddingRight: "8px",
                      scrollBehavior: "smooth",
                      "&::-webkit-scrollbar": {
                        width: "6px",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: "#f1f1f1",
                        borderRadius: "3px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#cbd5e1",
                        borderRadius: "3px",
                      },
                      "&::-webkit-scrollbar-thumb:hover": {
                        background: "#94a3b8",
                      },
                    }}
                  >
                    {strategyStats.topRisk?.map((strategy, index) => (
                      <StrategyStatItem
                        key={`risk-${index}`}
                        name={strategy.name}
                        amount={strategy.risk}
                        percentage={strategy.risk / NAV_NUMBER}
                        positions={strategy.numPositions}
                        isNegative={strategy.risk < 0}
                        isRisk={true}
                        rank={index + 1}
                        isExpanded={expandedRiskStrategies[strategy.name]}
                        onToggleExpand={() =>
                          setExpandedRiskStrategies((prev) => ({
                            ...prev,
                            [strategy.name]: !prev[strategy.name],
                          }))
                        }
                        details={getStrategyDetails(strategy.name)}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Right Column - PnL & Monthly Breakdown */}
        <Grid item xs={12} md={2}>
          <Grid container spacing={1}>
            {/* PnL Metrics Card */}
            <Grid item xs={12}>
              <Card
                sx={{
                  backgroundColor: "#ffffff",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                  borderRadius: "16px",
                  transition: "all 0.3s ease-in-out",
                  border: "1px solid rgba(0,0,0,0.05)",
                  "&:hover": {
                    boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent sx={{ padding: "16px" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      marginBottom: "16px",
                      fontSize: "1.1rem",
                      fontWeight: 700,
                      color: "#1a237e",
                      display: "flex",
                      alignItems: "center",
                      "&:before": {
                        content: '""',
                        display: "block",
                        width: "4px",
                        height: "24px",
                        backgroundColor: "#1a237e",
                        marginRight: "12px",
                        borderRadius: "2px",
                      },
                    }}
                  >
                    PnL Metrics
                  </Typography>

                  {/* Total PnL Section */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontSize: "0.95rem",
                        fontWeight: 600,
                        color: "#1a237e",
                        marginBottom: "12px",
                        paddingBottom: "8px",
                        borderBottom: "2px solid #e2e8f0",
                      }}
                    >
                      Total Deal Close PnL
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {(() => {
                        const grossPnL = MABData.reduce(
                          (sum, pos) =>
                            sum + (Number(pos.gross_pnl_to_dealclose) || 0),
                          0
                        );
                        const netPnL = MABData.reduce(
                          (sum, pos) =>
                            sum + (Number(pos.net_pnl_to_dealclose) || 0),
                          0
                        );
                        const annualizedNetPnL =
                          stats.weightedDuration > 0
                            ? netPnL * (365 / stats.weightedDuration)
                            : 0;

                        return (
                          <>
                            <StatItem
                              label="Gross PnL"
                              amount={grossPnL}
                              percentage={(grossPnL / NAV_NUMBER) * 100}
                              isNegative={grossPnL < 0}
                            />
                            <StatItem
                              label="Net PnL"
                              amount={netPnL}
                              percentage={(netPnL / NAV_NUMBER) * 100}
                              isNegative={netPnL < 0}
                            />
                            <StatItem
                              label="Annualized Net PnL"
                              amount={annualizedNetPnL}
                              percentage={(annualizedNetPnL / NAV_NUMBER) * 100}
                              isNegative={annualizedNetPnL < 0}
                            />
                          </>
                        );
                      })()}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Monthly PnL Breakdown Card */}
            <Grid item xs={12}>
              <Card
                sx={{
                  backgroundColor: "#ffffff",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                  borderRadius: "16px",
                  transition: "all 0.3s ease-in-out",
                  border: "1px solid rgba(0,0,0,0.05)",
                  "&:hover": {
                    boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent sx={{ padding: "16px" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      marginBottom: "16px",
                      fontSize: "1.1rem",
                      fontWeight: 700,
                      color: "#1a237e",
                      display: "flex",
                      alignItems: "center",
                      "&:before": {
                        content: '""',
                        display: "block",
                        width: "4px",
                        height: "24px",
                        backgroundColor: "#1a237e",
                        marginRight: "12px",
                        borderRadius: "2px",
                      },
                    }}
                  >
                    Monthly PnL Breakdown
                  </Typography>

                  {/* Column Labels */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "8px 16px",
                      backgroundColor: "#f8fafc",
                      borderRadius: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        color: "#1a237e",
                        width: "40%",
                        textAlign: "left",
                      }}
                    >
                      Month
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        color: "#1a237e",
                        width: "60%",
                        textAlign: "right",
                      }}
                    >
                      Gross PnL / Net PnL
                    </Typography>
                  </Box>

                  {/* Monthly Stats */}
                  <Box
                    sx={{
                      maxHeight: "40vh",
                      overflow: "auto",
                      overflowX: "hidden",
                      paddingRight: "8px",
                      scrollBehavior: "smooth",
                      "&::-webkit-scrollbar": {
                        width: "6px",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: "#f1f1f1",
                        borderRadius: "3px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#cbd5e1",
                        borderRadius: "3px",
                      },
                      "&::-webkit-scrollbar-thumb:hover": {
                        background: "#94a3b8",
                      },
                    }}
                  >
                    {getNextMonthsPnL(MABData).length === 0 ? (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#64748b",
                          fontStyle: "italic",
                          textAlign: "center",
                          padding: "16px 0",
                        }}
                      >
                        No upcoming deal closes
                      </Typography>
                    ) : (
                      getNextMonthsPnL(MABData)?.map((month, index) => (
                        <Box
                          key={month.monthYear}
                          sx={{
                            backgroundColor: "#ffffff",
                            borderRadius: "12px",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                            transition: "all 0.2s ease-in-out",
                            border: "1px solid #e2e8f0",
                            overflow: "hidden",
                            marginBottom: 2,
                            "&:hover": {
                              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                              transform: "translateY(-1px)",
                            },
                          }}
                        >
                          {/* Month Header - Clickable */}
                          <Box
                            onClick={() =>
                              setExpandedMonthlyPnL((prev) => ({
                                ...prev,
                                [month.monthYear]: !prev[month.monthYear],
                              }))
                            }
                            sx={{
                              padding: "12px 16px",
                              cursor: "pointer",
                              transition: "background-color 0.2s ease",
                              "&:hover": {
                                backgroundColor: "#f8fafc",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "flex-start",
                                  gap: 0.5,
                                  width: "30%",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "0.9rem",
                                    fontWeight: 600,
                                    color: "#1a237e",
                                  }}
                                >
                                  {new Date(
                                    month.year,
                                    month.month
                                  ).toLocaleString("default", {
                                    month: "short",
                                    year: "numeric",
                                  })}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#64748b",
                                    fontSize: "0.6rem",
                                    backgroundColor: "#e2e8f0",
                                    padding: "2px 6px",
                                    borderRadius: "10px",
                                  }}
                                >
                                  {month.strategies.length} Deals
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  width: "70%",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-end",
                                    gap: 0.25,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "0.9rem",
                                      fontWeight: 700,
                                      color:
                                        month.grossPnL < 0
                                          ? "#ef4444"
                                          : "#22c55e",
                                      lineHeight: 1.1,
                                      textAlign: "right",
                                    }}
                                  >
                                    {`$${formatAmount(
                                      month.grossPnL
                                    )} / $${formatAmount(month.netPnL)}`}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      color:
                                        month.netPnL < 0
                                          ? "#ef4444"
                                          : "#22c55e",
                                      fontSize: "0.75rem",
                                      fontWeight: 500,
                                      textAlign: "right",
                                    }}
                                  >
                                    {`${(
                                      (month.grossPnL / NAV_NUMBER) *
                                      10000
                                    ).toFixed(1)} / ${(
                                      (month.netPnL / NAV_NUMBER) *
                                      10000
                                    ).toFixed(1)} bps`}
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#64748b",
                                    fontSize: "0.8rem",
                                    fontWeight: 600,
                                    transform: expandedMonthlyPnL[
                                      month.monthYear
                                    ]
                                      ? "rotate(90deg)"
                                      : "rotate(0deg)",
                                    transition: "transform 0.2s ease",
                                  }}
                                >
                                  ▶
                                </Typography>
                              </Box>
                            </Box>
                          </Box>

                          {/* Expanded Strategy Details */}
                          {expandedMonthlyPnL[month.monthYear] &&
                            month.strategies.length > 0 && (
                              <Box
                                sx={{
                                  backgroundColor: "#f8fafc",
                                  borderTop: "1px solid #e2e8f0",
                                  padding: "8px",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                  }}
                                >
                                  {month.strategies.map(
                                    (strategy, strategyIndex) => (
                                      <Box
                                        key={strategy.name}
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          padding: "6px 10px",
                                          backgroundColor: "#ffffff",
                                          borderRadius: "6px",
                                          border: "1px solid #e2e8f0",
                                        }}
                                      >
                                        <Box>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 600,
                                              color: "#475569",
                                              fontSize: "0.7rem",
                                            }}
                                          >
                                            {strategy.name}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: "#64748b",
                                              fontSize: "0.6rem",
                                              display: "block",
                                            }}
                                          >
                                            {strategy.deals} positions
                                            {strategy.deals !== 1 ? "s" : ""}
                                          </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: "right" }}>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 700,
                                              color:
                                                strategy.grossPnL < 0
                                                  ? "#ef4444"
                                                  : "#22c55e",
                                              fontSize: "0.7rem",
                                              display: "block",
                                              lineHeight: 1.2,
                                              marginBottom: 0.5,
                                            }}
                                          >
                                            {`$${formatAmount(
                                              strategy.grossPnL
                                            )} / $${formatAmount(
                                              strategy.netPnL
                                            )}`}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: "#64748b",
                                              fontSize: "0.6rem",
                                              fontWeight: 500,
                                              display: "block",
                                            }}
                                          >
                                            {`${(
                                              (strategy.grossPnL / NAV_NUMBER) *
                                              10000
                                            ).toFixed(1)} / ${(
                                              (strategy.netPnL / NAV_NUMBER) *
                                              10000
                                            ).toFixed(1)} bps`}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    )
                                  )}
                                </Box>
                              </Box>
                            )}

                          {/* Show message if no strategies */}
                          {expandedMonthlyPnL[month.monthYear] &&
                            month.strategies.length === 0 && (
                              <Box
                                sx={{
                                  backgroundColor: "#f8fafc",
                                  borderTop: "1px solid #e2e8f0",
                                  padding: "8px",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#64748b",
                                    fontStyle: "italic",
                                    textAlign: "center",
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  No strategies with closing dates in this month
                                </Typography>
                              </Box>
                            )}
                        </Box>
                      ))
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Winner/Loser Box */}
        <Grid item xs={12} md={2.5}>
          <Grid container spacing={1}>
            {/* Today's Total PnL Card */}
            <Grid item xs={12}>
              <Card
                sx={{
                  backgroundColor: "#ffffff",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                  borderRadius: "16px",
                  transition: "all 0.3s ease-in-out",
                  border: "1px solid rgba(0,0,0,0.05)",
                  "&:hover": {
                    boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent sx={{ padding: "16px" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "1.1rem",
                      fontWeight: 700,
                      color: "#1a237e",
                      marginBottom: "16px",
                      display: "flex",
                      alignItems: "center",
                      "&:before": {
                        content: '""',
                        display: "block",
                        width: "4px",
                        height: "24px",
                        backgroundColor: "#1a237e",
                        marginRight: "12px",
                        borderRadius: "2px",
                      },
                    }}
                  >
                    Today's Total PnL
                  </Typography>
                  <Box
                    sx={{
                      marginBottom: "0px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 16px",
                      borderRadius: "12px",
                      backgroundColor:
                        MABData.reduce(
                          (sum, pos) => sum + (Number(pos.total_pnl) || 0),
                          0
                        ) < 0
                          ? "rgba(239, 68, 68, 0.05)"
                          : "rgba(34, 197, 94, 0.05)",
                      border: "1px solid",
                      borderColor:
                        MABData.reduce(
                          (sum, pos) => sum + (Number(pos.total_pnl) || 0),
                          0
                        ) < 0
                          ? "rgba(239, 68, 68, 0.2)"
                          : "rgba(34, 197, 94, 0.2)",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor:
                          MABData.reduce(
                            (sum, pos) => sum + (Number(pos.total_pnl) || 0),
                            0
                          ) < 0
                            ? "rgba(239, 68, 68, 0.08)"
                            : "rgba(34, 197, 94, 0.08)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "70%",
                        textAlign: "left",
                        position: "relative",
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          top: "50%",
                          right: 0,
                          transform: "translateY(-50%)",
                          width: "1px",
                          height: "70%",
                          background:
                            MABData.reduce(
                              (sum, pos) => sum + (Number(pos.total_pnl) || 0),
                              0
                            ) < 0
                              ? "rgba(239, 68, 68, 0.2)"
                              : "rgba(34, 197, 94, 0.2)",
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "1.1rem",
                          fontWeight: 700,
                          color: "#1a237e",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          marginLeft: "10px",
                        }}
                      >
                        PnL
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        width: "50%",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "1.1rem",
                          fontWeight: 700,
                          color:
                            MABData.reduce(
                              (sum, pos) => sum + (Number(pos.total_pnl) || 0),
                              0
                            ) < 0
                              ? "#ef4444"
                              : "#22c55e",
                        }}
                      >
                        {(() => {
                          const totalPnL = MABData.reduce(
                            (sum, pos) => sum + (Number(pos.total_pnl) || 0),
                            0
                          );
                          return totalPnL < 0
                            ? `($${Math.abs(
                                Math.round(totalPnL)
                              ).toLocaleString()})`
                            : `$${Math.round(totalPnL).toLocaleString()}`;
                        })()}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        width: "50%",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "1.1rem",
                          fontWeight: 700,
                          color:
                            MABData.reduce(
                              (sum, pos) => sum + (Number(pos.total_pnl) || 0),
                              0
                            ) < 0
                              ? "#ef4444"
                              : "#22c55e",
                          marginRight: "10px",
                        }}
                      >
                        {(() => {
                          const totalPnL = MABData.reduce(
                            (sum, pos) => sum + (Number(pos.total_pnl) || 0),
                            0
                          );
                          const bps = ((totalPnL / NAV_NUMBER) * 10000).toFixed(
                            1
                          );
                          return totalPnL < 0 ? `${bps}` : `+${bps}`;
                        })()}
                        {" bps"}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Daily Top Performers Card */}
            <Grid item xs={12}>
              <Card
                sx={{
                  backgroundColor: "#ffffff",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                  borderRadius: "16px",
                  transition: "all 0.3s ease-in-out",
                  border: "1px solid rgba(0,0,0,0.05)",
                  "&:hover": {
                    boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent sx={{ padding: "16px" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "1.1rem",
                      fontWeight: 700,
                      color: "#1a237e",
                      marginBottom: "4px",
                      display: "flex",
                      alignItems: "center",
                      "&:before": {
                        content: '""',
                        display: "block",
                        width: "4px",
                        height: "24px",
                        backgroundColor: "#1a237e",
                        marginRight: "12px",
                        borderRadius: "2px",
                      },
                    }}
                  >
                    Daily Top Performers
                  </Typography>
                  <WinnerLoserBox
                    strategies={getStrategyPnL(MABData)
                      ?.filter((strategy) => strategy.pnl > 0)
                      .sort((a, b) => b.pnl - a.pnl)}
                    NAV_NUMBER={NAV_NUMBER}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Daily Worst Losers Card */}
            <Grid item xs={12}>
              <Card
                sx={{
                  backgroundColor: "#ffffff",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                  borderRadius: "16px",
                  transition: "all 0.3s ease-in-out",
                  border: "1px solid rgba(0,0,0,0.05)",
                  "&:hover": {
                    boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent sx={{ padding: "16px" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "1.1rem",
                      fontWeight: 700,
                      color: "#1a237e",
                      marginBottom: "4px",
                      display: "flex",
                      alignItems: "center",
                      "&:before": {
                        content: '""',
                        display: "block",
                        width: "4px",
                        height: "24px",
                        backgroundColor: "#1a237e",
                        marginRight: "12px",
                        borderRadius: "2px",
                      },
                    }}
                  >
                    Daily Worst Performers
                  </Typography>
                  <WinnerLoserBox
                    strategies={getStrategyPnL(MABData)
                      ?.filter((strategy) => strategy.pnl < 0)
                      .sort((a, b) => a.pnl - b.pnl)}
                    NAV_NUMBER={NAV_NUMBER}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MABSummaryStats;
